import { Button, Icon, IconButton, Input } from "~/ui-library";
import { GiftIcon, XMarkIcon, LinkIcon } from "@heroicons/react/24/outline";
import { Popover } from "@headlessui/react";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { useGiftLink } from "~/components/GiftLinkShareButton/useGiftLink";
import { commonConfig } from "~/config/common-config";
import { CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/24/solid";
import { getButtonStyles } from "~/ui-library/shared-styles/shared-button-styles";

const GIFT_LINK_PLACEHOLDER = `${commonConfig.NEXT_PUBLIC_ENDIL_URL}/...`;

const buttonDefaultStyles = getButtonStyles({
  variant: "default",
  size: "default",
});

export const GiftLinkShareButton: React.FC<{
  postId: string | number;
  className?: string;
}> = ({ postId, className }) => {
  const {
    canShowGiftLink,
    monthlyBalance,
    getGiftLink,
    userAlreadyGiftedPost,
    isGeneratingGiftLink,
    isLoadingBalance,
    giftLink,
    copyGiftLink,
    isCopiedFeedback,
  } = useGiftLink(postId);

  const { icon: actionIcon, label: actionLabel } = (() => {
    if (isGeneratingGiftLink) {
      return {
        icon: <LoadingSpinner width={24} height={24} />,
        label: "Creating...",
      };
    }

    if (isCopiedFeedback) {
      return {
        icon: <CheckIcon className="size-5" />,
        label: "Copied!",
      };
    }

    if (giftLink) {
      return {
        icon: <DocumentDuplicateIcon className="size-5" />,
        label: "Copy link",
      };
    }

    if (userAlreadyGiftedPost) {
      return {
        icon: <LinkIcon className="size-5" />,
        label: "Get link",
      };
    }

    return {
      icon: <LinkIcon className="size-5" />,
      label: "Create link",
    };
  })();

  if (!canShowGiftLink) return null;

  return (
    <>
      <Popover className="relative">
        <Popover.Button
          className={className || buttonDefaultStyles}
          aria-label="Gift this article"
        >
          <div className="flex items-center gap-xxs">
            <Icon SvgIcon={GiftIcon} className="fill-transparent" />
            Gift this link
          </div>
        </Popover.Button>
        <Popover.Panel className="absolute -left-xxl z-10 mt-2 w-72 rounded-lg bg-white p-4 shadow-3 md:-left-l md:w-80 lg:w-96 lg:p-6">
          <div className="relative flex items-center justify-between">
            <div className="max-w-[85%] text-pretty break-keep pb-3 text-body-large font-semibold">
              Provide someone in your network access to this analysis
            </div>

            <Popover.Button
              as={IconButton}
              className="absolute -right-2 -top-2"
              aria-label="Close gift link share modal"
              SvgIcon={XMarkIcon}
              size="large"
              variant="subtle"
            />
          </div>
          <div className="text-pretty break-keep border-b-1 border-solid border-gray-300 pb-xs">
            Your subscription enables you to share up to{" "}
            <span className="font-bold">
              {commonConfig.NEXT_PUBLIC_GIFT_LINK_MONTHLY_QUOTA}
            </span>{" "}
            un-paywalled links each month.
          </div>
          {isLoadingBalance ? (
            <div
              aria-label="Loading gift link"
              className="flex justify-center pt-4"
            >
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="w-full py-xs text-center">
                <span className="text-headline4 font-bold">
                  {`${monthlyBalance} gift link${
                    monthlyBalance === 1 ? "" : "s"
                  } remaining`}
                </span>
              </div>
              {(monthlyBalance > 0 || userAlreadyGiftedPost) && (
                <div className="flex w-full flex-col items-center gap-xxs">
                  <div className="relative w-full">
                    <Input
                      aria-label="Gift link field"
                      value={giftLink || ""}
                      readOnly={!!giftLink}
                      disabled={!giftLink}
                      placeholder={GIFT_LINK_PLACEHOLDER}
                      fullWidth
                    />

                    <Button
                      aria-label="Get gift link"
                      disabled={isGeneratingGiftLink || isCopiedFeedback}
                      size="fit"
                      className="absolute right-0 top-0 p-xs opacity-100"
                      onClick={giftLink ? copyGiftLink : getGiftLink}
                    >
                      <div className="flex flex-row items-center gap-xxs">
                        {actionIcon}
                        {actionLabel}
                      </div>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Popover.Panel>
      </Popover>
    </>
  );
};
